import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { CryptoDigestAlgorithm, digestStringAsync } from 'expo-crypto';
import React, { useCallback, useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import { HandlerStateChangeEvent, LongPressGestureHandler, State } from 'react-native-gesture-handler';

import { DEVELOPER_PASSWORD_DIGEST } from '~/app/constants';
import HorizontalLogoSvg from '~/assets/images/horizontal-logo.svg';
import { Button } from '~/components/button';
import { AppNavigatorParamList } from '~/navigator/app-navigator';

type HorizontalLogoNavigation = StackNavigationProp<AppNavigatorParamList>;

type Props = {
    devMenuAccess?: boolean;
};

export const HorizontalLogo: React.FC<Props> = props => {
    const { devMenuAccess = false } = props;

    const { navigate } = useNavigation<HorizontalLogoNavigation>();

    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [password, setPassword] = useState<string>('');

    const onGestureEvent = useCallback(
        (event: HandlerStateChangeEvent) => {
            if (event.nativeEvent.state === State.ACTIVE) {
                if (__DEV__) {
                    navigate('developer');
                } else {
                    setShowDialog(true);
                }
            }
        },
        [navigate]
    );

    const closeDialog = useCallback(() => {
        setPassword('');
        setShowDialog(false);
    }, []);

    const onSubmit = useCallback(async () => {
        const digest = await digestStringAsync(CryptoDigestAlgorithm.SHA256, password);

        if (digest === DEVELOPER_PASSWORD_DIGEST) {
            navigate('developer');
        }

        setPassword('');
        setShowDialog(false);
    }, [navigate, password]);

    if (!devMenuAccess) {
        return <HorizontalLogoSvg />;
    }

    return (
        <>
            <Modal visible={showDialog} transparent animationType="fade" onRequestClose={closeDialog}>
                <View style={styles.overlay}>
                    <View style={styles.dialog}>
                        <Text style={styles.title}>Enter password</Text>
                        <TextInput
                            autoFocus
                            autoCorrect={false}
                            autoCapitalize="none"
                            placeholder="Password"
                            secureTextEntry
                            onChangeText={setPassword}
                            style={styles.input}
                        />
                        <Button type="primary" label="Open" disabled={!password} onPress={onSubmit} />
                        <Pressable onPress={closeDialog} style={styles.cancelButton}>
                            <Text style={styles.cancelText}>Cancel</Text>
                        </Pressable>
                    </View>
                </View>
            </Modal>

            <LongPressGestureHandler testID="horizontal-logo-gesture-handler" onHandlerStateChange={onGestureEvent}>
                <Pressable>
                    <HorizontalLogoSvg />
                </Pressable>
            </LongPressGestureHandler>
        </>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center'
    },
    dialog: {
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 24,
        width: '80%',
        maxWidth: 400
    },
    title: {
        fontSize: 18,
        fontWeight: '600',
        marginBottom: 16
    },
    input: {
        borderWidth: 1,
        borderColor: '#ccc',
        borderRadius: 8,
        padding: 12,
        marginBottom: 16,
        fontSize: 16
    },
    cancelButton: {
        marginTop: 12,
        alignItems: 'center'
    },
    cancelText: {
        fontSize: 16,
        color: '#666'
    }
});
